import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { ArrowLeft, Video, Mic, Square, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { attachJitsiLeaveHandler, jitsiConfigOverwrite, jitsiInterfaceConfigOverwrite, loadJitsiApi } from '@/lib/jitsi';

export default function ConferenceRoom() {
  const { conferenceId } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const jitsiRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1000) { toast.error('Aufnahme zu kurz'); return; }
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append('file', new File([blob], 'aufnahme.webm', { type: 'audio/webm' }));
          await api.post(`/conferences/${conferenceId}/recording`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          toast.success('Aufnahme hochgeladen — KI-Protokollentwurf wird erstellt und per E-Mail an Sie gesendet');
        } catch (e) {
          const d = e.response?.data?.detail;
          toast.error(typeof d === 'string' ? d : 'Upload fehlgeschlagen');
        } finally { setUploading(false); }
      };
      rec.start(1000);
      recRef.current = rec;
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
      setRecording(true);
      toast.info('Aufnahme läuft (Mikrofon) — beim Stoppen wird automatisch ein KI-Protokoll erstellt');
    } catch {
      toast.error('Mikrofonzugriff verweigert — Aufnahme nicht möglich');
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    setRecording(false);
    try { recRef.current?.stop(); } catch { /* noop */ }
  };

  useEffect(() => () => { clearInterval(timerRef.current); try { recRef.current?.state === 'recording' && recRef.current.stop(); } catch { /* noop */ } }, []);

  useEffect(() => {
    api.get(`/conferences/${conferenceId}/join`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Konferenzraum konnte nicht geladen werden'));
  }, [conferenceId]);

  useEffect(() => {
    if (!info) return undefined;
    let disposed = false;

    const init = () => {
      if (disposed || !window.JitsiMeetExternalAPI) return;
      const container = document.getElementById('jitsi-conf-container');
      if (!container) return;
      const j = new window.JitsiMeetExternalAPI(info.domain, {
        roomName: info.room,
        parentNode: container,
        width: '100%',
        height: '100%',
        configOverwrite: jitsiConfigOverwrite({
          prejoinPageEnabled: true,
          subject: info.subject,
          toolbarButtons: ['microphone', 'camera', 'desktop', 'chat', 'tileview', 'raisehand', 'participants-pane', 'settings', 'hangup'],
        }),
        interfaceConfigOverwrite: jitsiInterfaceConfigOverwrite(),
        userInfo: { displayName: info.display_name },
      });
      j.addEventListener('videoConferenceJoined', () => {
        j.executeCommand('displayName', info.display_name);
        j.executeCommand('subject', info.subject);
      });
      attachJitsiLeaveHandler(j, () => {
        jitsiRef.current = null;
        navigate('/conferences', { replace: true });
      });
      jitsiRef.current = j;
    };

    loadJitsiApi(info.domain).then(init).catch(() => {
      setError('Jitsi-Skript konnte nicht geladen werden');
    });
    return () => {
      disposed = true;
      try { jitsiRef.current?.dispose(); } catch { /* noop */ }
    };
  }, [info]);

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/conferences')} data-testid="conference-room-back">
              <ArrowLeft className="h-4 w-4 mr-1" /> Zurück
            </Button>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Video className="h-5 w-5 text-brand-red" /> {info?.title || 'Konferenz'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {recording && (
              <span className="text-sm font-mono text-red-600 flex items-center gap-1.5" data-testid="recording-timer">
                <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                {String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')}
              </span>
            )}
            {uploading ? (
              <Button variant="outline" disabled data-testid="recording-uploading">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird verarbeitet…
              </Button>
            ) : recording ? (
              <Button onClick={stopRecording} className="bg-red-600 hover:bg-red-700" data-testid="recording-stop-btn">
                <Square className="h-4 w-4 mr-2" /> Aufnahme beenden
              </Button>
            ) : (
              <Button variant="outline" onClick={startRecording}
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      title="Nimmt Audio über Ihr Mikrofon auf und erstellt danach automatisch einen KI-Protokollentwurf"
                      data-testid="recording-start-btn">
                <Mic className="h-4 w-4 mr-2" /> Aufnahme starten
              </Button>
            )}
          </div>
        </div>
        {error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 space-y-3">
            <p>{error}</p>
            {info?.url && (
              <Button
                variant="outline"
                onClick={() => window.open(info.url, '_blank', 'noopener,noreferrer')}
              >
                Meeting im neuen Tab öffnen
              </Button>
            )}
          </div>
        ) : (
          <div id="jitsi-conf-container" data-testid="jitsi-conference-container"
               className="w-full rounded-xl overflow-hidden border border-slate-200 bg-slate-900"
               style={{ height: 'calc(100vh - 180px)' }} />
        )}
      </div>
    </Layout>
  );
}
